import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import type { Camera } from "~/entities/camera/types";
import { updateCameraAnalysisNumber } from "../api/update-camera-analysis-number";
import { CAMERAS_QUERY_KEY } from "./use-cameras";

/**
 * analysisNumber가 null인 카메라에 N번호를 자동으로 서버에 저장.
 * 한 번 부여된 N번호는 다시 바뀌지 않도록 서버에 즉시 PATCH한다.
 */
export function useAssignNNumbers(
  cameras: Camera[],
  branchUid: string | undefined,
  versionId: number | undefined,
  options?: { onErrors?: (failedCount: number) => void },
) {
  const queryClient = useQueryClient();
  const patchingRef = useRef(false);
  const patchedIdsRef = useRef(new Set<number>());
  const onErrorsRef = useRef(options?.onErrors);
  onErrorsRef.current = options?.onErrors;

  useEffect(() => {
    if (!branchUid || cameras.length === 0 || patchingRef.current) return;

    const nullCameras = cameras.filter(
      (c) => c.analysisNumber === null && !patchedIdsRef.current.has(c.id),
    );
    if (nullCameras.length === 0) return;

    // 이미 사용 중인 N번호 수집 + 현재 최대값 추적
    // (4-5-3) 빈 번호를 채우지 않고 max + 1 부터 뒤로 쌓는다 — 한 번 빠진 번호는 영구 비움
    const usedNNumbers = new Set<string>();
    let maxN = 0;
    for (const cam of cameras) {
      if (cam.analysisNumber != null && cam.analysisNumber.startsWith("N")) {
        usedNNumbers.add(cam.analysisNumber);
        const v = parseInt(cam.analysisNumber.slice(1), 10);
        if (Number.isFinite(v) && v > maxN) maxN = v;
      }
    }

    patchingRef.current = true;
    let failedCount = 0;

    (async () => {
      for (const cam of nullCameras) {
        // 항상 현재 최대값 + 1 부터 (충돌 시 더 증가)
        let n = maxN + 1;
        while (usedNNumbers.has(`N${n}`)) n++;
        const num = `N${n}`;
        usedNNumbers.add(num);
        maxN = n;
        try {
          await updateCameraAnalysisNumber({
            branchUid,
            versionId,
            cameraOrientationId: cam.id,
            analysisNumber: num,
          });
          patchedIdsRef.current.add(cam.id);
        } catch (e) {
          failedCount += 1;
          console.error(`[useAssignNNumbers] Failed to assign ${num} to camera ${cam.id}:`, e);
        }
      }
      patchingRef.current = false;
      if (failedCount > 0) onErrorsRef.current?.(failedCount);
      // PATCH 완료 후 카메라 목록 refetch
      queryClient.invalidateQueries({ queryKey: [CAMERAS_QUERY_KEY, branchUid, versionId] });
    })();
  }, [cameras, branchUid, versionId, queryClient]);
}
